import { useSyncPreferredLocale, useT } from '@foir/i18n';
import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  lazy,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
// Apollo removed - all data fetching via TanStack Query + connect clients
import { useIdentityClient } from "./platform-context";
import { triggerAuthRedirect } from "./auth-redirect";
import { isTransient, retryAfterSeconds } from "@foir/rpc-web/errors";
import { grantConfinement } from "./grant-confinement";
import type { ImpersonationPrompt } from "./impersonation-request";

// Lazy, because every console app mounts this provider and only staff who are
// refused reach ever see the dialog. Statically importing it pulled @foir/ui's
// dialog, OTP input and textarea plus sonner into every one of them — measured
// at 17x the cold module cost of this file (55ms -> 922ms), which is what put
// the packages/auth suite over its 5s timeout on a two-core runner.
const ImpersonationRequestDialog = lazy(() =>
  import("./impersonation-request").then((m) => ({
    default: m.ImpersonationRequestDialog,
  })),
);
// Same chunk, same reason: the standing "ask for access" affordance
// is only ever rendered for a staff sitting in a workspace they cannot
// read, so it must not cost every console app a module either.
const ImpersonationRequestBanner = lazy(() =>
  import("./impersonation-request").then((m) => ({
    default: m.ImpersonationRequestBanner,
  })),
);
import type {
  User as ConnectUser,
  ImpersonationGrant,
  Project as ConnectProject,
  SessionContext as ConnectSessionContext,
  Tenant as ConnectTenant,
} from "@foir/proto-ts/identity/v1/identity_pb";
import type { ReactNode } from "react";

/** The signed-in person. */
type SessionUser = ConnectUser;
type TenantInfo = ConnectTenant;
type ProjectInfo = ConnectProject;

/**
 * The standing ask-for-access affordance for a workspace the user is IN but
 * cannot read the content of.
 *
 * Non-null only while the session is in such a workspace — staff who entered
 * without an approved grant. It goes null the moment reach is live or the
 * session moves elsewhere.
 *
 * `request()` re-raises the inline dialog. It MINTS NOTHING and changes no
 * authority: it is the same ask the entry click already offered, kept reachable
 * so that dismissing it is never terminal.
 */
export interface WorkspaceAccessRequest {
  tenantId: string;
  /** Display name; falls back to the id when the session cannot resolve one. */
  tenantName: string;
  /** A request already awaiting approval for this workspace, if any. */
  pendingGrant: ImpersonationGrant | null;
  /** Raise the ask-for-access dialog for this workspace. */
  request: () => void;
}

/**
 * What ONE dismissal covers: the scope the ask was raised for.
 *
 * Keyed by the confinement the ask would actually CARRY, so it is read off
 * ./grant-confinement.ts rather than re-deciding which rungs count. A dismissal
 * is a statement about one ask; keying it by a rung the request drops would
 * make it cover an ask nobody was shown. And a dismissal in one organisation
 * must not silence the ask in its sibling, because a grant confined to one
 * confers nothing in the other and the user has said nothing about it.
 */
function promptScopeKey(prompt: ImpersonationPrompt): string {
  const { projectId, orgId } = grantConfinement({
    projectId: prompt.projectId ?? "",
    orgId: prompt.orgId ?? "",
  });
  return [prompt.tenantId, projectId, orgId].filter(Boolean).join(":");
}

/**
 * Session Context Interface
 *
 * Unified context providing both authentication and session-based tenant/project context.
 * Unchanged from the Apollo version — consumers don't need to change.
 */
export interface SessionContextValue {
  // Authentication
  user: SessionUser | null;
  /**
   * `role` is removed. It was `sessionData.effectiveRole` cast straight
   * into a six-value `UserRole` enum — an unchecked cast from an untyped wire
   * string, and the whole reason this console had a rank ladder.
   *
   * The server has stopped sending it. A session collapses a SET of assignments
   * and a lattice has no maximum, so the field could only ever have been the
   * first row the query happened to return. Gate on the verb below, and read
   * `platformPermissions` (through `useIsStaff`) for the staff plane.
   */
  /**
   * The console's effective permission verbs for the CURRENT (tenant, project)
   * scope — the same set the platform PDP enforces (e.g. "records:write.all",
   * "hooks:write"), with the authority-key implications already expanded server
   * side. Gate write controls via the generated `useCaps()` accessor (e.g.
   * `caps.hooks.write`) on the actual verb, so a tenant-composed role resolves
   * exactly as the backend does. Empty array when unauthenticated.
   */
  effectivePermissions: string[];
  /**
   * What the person holds at the platform itself (`platform:manage`,
   * `platform:create_workspace`), resolved live by the platform. The staff
   * screens open on `platform:manage`, the permission the platform's own staff
   * gate reads.
   */
  platformPermissions: string[];
  /**
   * What the person holds at the workspace itself, apart from any project in
   * it. `effectivePermissions` adds what a project's roles give where the
   * session stands in one, and those do not reach the workspace's own
   * configuration. Read it through `useWorkspaceCaps()`.
   */
  workspacePermissions: string[];
  isAuthenticated: boolean;
  /** True while the session is first read, AND while a switch is in flight. */
  isLoading: boolean;
  /**
   * True only until the session has been read once. A gate that hides the app
   * reads this, never `isLoading`: hiding it for a switch unmounts the screen
   * that asked for the switch, and a refused one was then asked again by the
   * remounted screen, forever.
   */
  isInitialLoading: boolean;
  error: Error | undefined;

  // Authentication actions. Signing in is the login app's: every console
  // signs in there, as an app, on the one login page.
  logout: () => Promise<void>;
  refetchUser: () => Promise<void>;

  // Current context
  tenantId: string | null | undefined;
  projectId: string | null | undefined;
  /**
   * The ORG the console stands in: the organization level, a partition
   * INSIDE the project. Null means the project-shared view, which is where a
   * console without an organisation in its address stands.
   *
   * It is a CONTAINMENT, not a filter: the row layer confines every read to it,
   * so changing it changes what the server returns rather than what the browser
   * shows. Treat it the way `projectId` is treated — a scope, not a preference.
   */
  orgId: string | null | undefined;
  /**
   * The selected org's display name, carried on the session so a cold load can
   * NAME the partition rather than printing a cuid2 while a list is in flight.
   * Same reason `tenantName` rides the session context.
   */
  orgName: string | null | undefined;

  // Available options
  availableTenants: Array<TenantInfo>;
  availableProjects: Array<ProjectInfo>;
  projectsInCurrentTenant: Array<ProjectInfo>;

  // Context actions. Nothing is stored: each reads the session context for
  // the place it names, and the console then names that place on its requests
  // (its address bar, through the request-scope interceptor).
  switchTenant: (tenantId: string) => Promise<
    | {
        tenantId: string | null;
        projectId: string | null;
        availableProjects: Array<ProjectInfo>;
      }
    | undefined
  >;
  switchProject: (projectId: string) => Promise<
    | {
        tenantId: string | null;
        projectId: string | null;
      }
    | undefined
  >;
  /**
   * Stand in an org, or return to the project-shared view with `null`.
   *
   * ONE AT A TIME, deliberately: there is no "every org" argument, because a
   * read that spans the boundary would make the boundary decorative. Totals
   * across orgs are summed from per-org counters instead, and are a different
   * surface.
   *
   * It reads the context for the organisation, and rejects when the platform
   * refuses the place (a suspended organisation). No cached answer changes
   * meaning: each was fetched for the place its request named, and a request
   * naming no organisation stands at the project.
   */
  selectOrganization: (orgId: string | null) => Promise<void>;
  /**
   * staff "enter this workspace" action. Use this as the explicit
   * fallback when switchTenant/switchProject is denied for a user without
   * a standing grant.
   *
   * This MINTS NOTHING and stores nothing. It reads the session context for the
   * workspace (staff reach the identity plane of any workspace without a
   * grant), and the workspace's
   * CONTENT stays behind a grant a workspace manager approved. `grant` is the
   * pre-existing live grant covering the entry (null when there is none, or when
   * entry was via a role).
   *
   * Entering without reach is not an error and does not throw: the provider
   * raises the inline request-access dialog on the same click, so callers need
   * no handling of their own.
   */
  enterTenant: (
    tenantId: string,
    projectId?: string,
  ) => Promise<
    | {
        tenantId: string | null;
        projectId: string | null;
        availableProjects: Array<ProjectInfo>;
        grant: ImpersonationGrant | null;
      }
    | undefined
  >;

  /**
   * The standing ask-for-access affordance for the current workspace, or null
   * when there is nothing to ask for. See {@link WorkspaceAccessRequest}.
   *
   * The provider already renders a persistent affordance of its own, so an app
   * only reads this to put the same ask somewhere more specific — an empty
   * state that would otherwise just look like an empty workspace.
   */
  workspaceAccess: WorkspaceAccessRequest | null;

  // Computed
  canSelectTenant: boolean;
  canSelectProject: boolean;
}

const SessionContext = createContext<SessionContextValue | null>(null);

/**
 * Session Context Provider
 *
 * Uses the ConnectRPC identity service for auth and session management. Every
 * call carries the console's token, traded for the login page's session by the
 * platform client.
 *
 * STABILITY CONTRACT: The context value reference must only change when the
 * actual identity/state changes.
 */
export interface SessionProviderHooks {
  /** Called when a session is identified (e.g. analytics / error-tracking identify). */
  onIdentify?: (user: {
    id: string;
    email: string;
    tenantId?: string;
    projectId?: string;
  }) => void;
  /** Called on logout (e.g. analytics / error-tracking reset). */
  onReset?: () => void;
  /** Called during logout for app-specific cleanup (e.g. clearing cached tokens). */
  onLogoutCleanup?: () => void | Promise<void>;
}

export function SessionContextProvider({
  children,
  onIdentify,
  onReset,
  onLogoutCleanup,
  // Whether this app asks for workspace access when the session cannot read the
  // tenant's content. TRUE for the tenant consoles, where a user is acting
  // AS the tenant and needs a way through.
  //
  // OPT-IN, and it used to default to true. Every app that mounts
  // this provider inherited the ask, including the AUTH app — so
  // /forgot-password and /reset-password opened a modal reading "You are in Tiny
  // Tiger Studio but cannot read its content" directly over the only form on the
  // page, with the standing bar beneath it. Unreachable email field, unreachable
  // password fields, a workspace named on a surface whose whole job is to be
  // identity-agnostic, and all of it in front of somebody who reached that page
  // because they are already locked out.
  //
  // A default of true means the question "does this app want a workspace
  // affordance?" is answered by not thinking about it. Defaulting to false makes
  // the two consoles that want it say so, and every future app safe without
  // knowing this trap exists.
  //
  // Explicit rather than sniffed from the host: the provider should not have to
  // know the deployment topology to decide what it renders.
  asksForWorkspaceAccess = false,
  // Whether a session of Fóir's console can be held where this app is served.
  // FALSE on a project's own login domain: the login app is served there too,
  // the console's session never is, and asking for it there is a call the
  // domain does not answer. Nobody is then signed in to the console, and the
  // provider says so without asking.
  readsSession = true,
}: { children: ReactNode; asksForWorkspaceAccess?: boolean; readsSession?: boolean } & SessionProviderHooks) {
  const t = useT();
  const identity = useIdentityClient();
  const queryClient = useQueryClient();

  const [user, setUser] = useState<SessionUser | null>(null);

  // The user's stored language drives the console's, not just their
  // notifications. It is pushed UP into the i18n provider rather than passed
  // down because that provider deliberately wraps this one: the sign-in and
  // session-error screens have to be translated too, and they render before
  // there is a session to read. Absent means they never chose, which leaves the
  // browser's own languages in charge.
  useSyncPreferredLocale(user?.preferredLocale);
  const [sessionData, setSessionData] = useState<ConnectSessionContext | null>(
    null,
  );
  // Read by the switches, which name the place they move from without taking
  // the session as a dependency (the context value keeps its stable identity).
  const sessionDataRef = useRef<ConnectSessionContext | null>(null);
  sessionDataRef.current = sessionData;
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  // Read by the first read's retry, which must not take the state as a
  // dependency (see fetchSession).
  const initialLoadCompleteRef = useRef(false);
  initialLoadCompleteRef.current = initialLoadComplete;
  const [error, setError] = useState<Error | undefined>(undefined);
  const [isMutating, setIsMutating] = useState(false);

  // Fetch session context on mount.
  // noteScope is declared below; fetchSession reaches it through a ref so the
  // load path can raise the same ask every scope move raises without reordering
  // the file or taking a dependency that would recreate the callback.
  const noteScopeRef = useRef<
    ((context: ConnectSessionContext | null | undefined) => void) | null
  >(null);

  // The first read, asked again after a failure that passes. See the catch below.
  const firstReadRetry = useRef<{ attempts: number; timer?: ReturnType<typeof setTimeout> }>({ attempts: 0 });
  const fetchSessionRef = useRef<(() => Promise<void>) | null>(null);
  useEffect(() => () => clearTimeout(firstReadRetry.current.timer), []);

  const fetchSession = useCallback(async () => {
    let askingAgain = false;
    try {
      const data = await identity.getSessionContext();
      firstReadRetry.current.attempts = 0;
      if (data?.user) {
        setSessionData(data);
        // A refresh or a bookmark performs no entry, so this is the only place
        // the ask can be raised on a cold load.
        noteScopeRef.current?.(data);
        const newUser = data.user ?? null;
        setUser((prev: SessionUser | null) => {
          if (
            prev &&
            newUser &&
            prev.id === newUser.id &&
            prev.email === newUser.email
          ) {
            return prev;
          }
          return newUser;
        });

        setError(undefined);
      } else {
        setUser(null);
        setSessionData(null);
      }
    } catch (err) {
      // What `setError` below stores is the Error OBJECT, not a sentence to put
      // in front of anyone — the state is typed `Error | undefined`. errors.ts
      // is explicit that the ConnectError has to survive intact, because
      // flattening it to a string here would take `isPermissionDenied` /
      // `isUnauthenticated` with it, and with them the withheld-read
      // classification and the retry policy. No consumer reads `error` off the
      // context today; whichever one eventually does is the place that owes the
      // reader a `userFacingReason`, at the point of render.
      const message = err instanceof Error ? err.message : String(err);
      if (
        message.includes("unauthenticated") ||
        message.includes("[unauthenticated]")
      ) {
        setUser(null);
        setSessionData(null);
        console.debug("Session not authenticated", {
          component: "SessionContext",
          operation: "fetch-session",
        });
      } else if (!initialLoadCompleteRef.current && isTransient(err)) {
        // The first read failed for a reason that passes: the session asked
        // for tokens faster than it may, or the platform could not be reached.
        // Nobody is known to be signed out, so the first load is not over:
        // ending it here would leave no user, and every app's gate would send
        // the person to the login page, which hands them straight back into
        // the same failure. Ask again, after what the platform said to wait,
        // or on a back-off of up to a minute.
        firstReadRetry.current.attempts += 1;
        const backoff = 2 ** Math.min(firstReadRetry.current.attempts - 1, 5);
        const wait = Math.min((retryAfterSeconds(err) ?? backoff) * 1000, 60_000);
        askingAgain = true;
        console.warn("Session context not read yet, asking again", {
          component: "SessionContext",
          operation: "fetch-session",
          waitMs: wait,
        });
        firstReadRetry.current.timer = setTimeout(() => {
          void fetchSessionRef.current?.();
        }, wait);
      } else {
        setError(err instanceof Error ? err : new Error(message));
        console.error(
          "Failed to fetch session context",
          err instanceof Error ? err : new Error(message),
          { component: "SessionContext", operation: "fetch-session" },
        );
      }
    } finally {
      // Set unless the first read is being asked again: React bails out when
      // already true, so no guard is needed. Keeping `initialLoadComplete` out
      // of the deps is what stops the mount fetch (and onIdentify) from firing
      // twice — the guard read used to put it in the deps, recreating the
      // callback after the first fetch and re-running the effect below.
      if (!askingAgain) setInitialLoadComplete(true);
    }
    // Only `identity` is a real dependency (the setState setters are stable).
    // Identify is handled by the effect below, keyed on the identity tuple.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identity]);
  fetchSessionRef.current = fetchSession;

  useEffect(() => {
    if (!readsSession) {
      setInitialLoadComplete(true);
      return;
    }
    fetchSession();
  }, [fetchSession, readsSession]);

  // ── Impersonation request prompt ───────────────────
  //
  // EnterTenant mints nothing now, so a staff entry can succeed and still not
  // reach the workspace's content. `noteScope` is called by EVERY path that
  // moves the session's scope, with the session the server returned; when reach
  // is missing it raises the inline ask-for-access dialog on that same click.
  // See impersonation-request.tsx for why this lives here (one mount, every
  // entry path) rather than in each app.
  const [impersonationPrompt, setImpersonationPrompt] =
    useState<ImpersonationPrompt | null>(null);
  // Latches on the first raise and never clears — see the mount site below.
  const [everPrompted, setEverPrompted] = useState(false);
  // The workspace the session is in WITHOUT content reach, held for as long as
  // the session stays in it. The dialog is a moment; this is a state, and it is
  // what makes the ask persistent — the banner below and `workspaceAccess` both
  // read it, so a dismissed dialog leaves the ask reachable instead of gone.
  const [reachlessWorkspace, setReachlessWorkspace] =
    useState<ImpersonationPrompt | null>(null);
  // The scopes whose ask the user dismissed, for as long as ONE entry into
  // a workspace lasts.
  //
  // A SET, keyed per scope (see `promptScopeKey`). It was a single slot while
  // the ask was only raised on entering a workspace, and a single slot is wrong
  // the moment moving between projects and organisations raises it too: dismiss
  // in Northwind, dismiss in Contoso, go back to Northwind, and a slot holding
  // only Contoso asks again about the thing the user already declined.
  //
  // It outranks every entry that was already in flight when the user
  // dismissed: a single arrival can fire two entries — the console mounts the
  // deep-link applier and the path-scope hook at once, and a URL carrying both
  // enters twice — and the slower one must not re-open what was just closed.
  //
  // It is emptied by every entry into a workspace that STARTS after it (see
  // `beginEntry`), so re-entering the workspace asks again. Moving between the
  // workspace's projects and organisations is not an entry and spends nothing.
  //
  // This used to clear only when reach turned TRUE. One "Not now" then
  // silenced the ask for that workspace permanently, and the only thing that
  // could restore it was already holding the reach it would have asked for —
  // which is the blank-workspace failure the staff-stay-dark decision was
  // argued not to have. A dismissal suppresses the DIALOG only: the standing
  // affordance below is recorded either way, so the ask is never gone.
  const dismissedScopesRef = useRef<Set<string>>(new Set());

  /**
   * Start of a deliberate entry into a workspace. A fresh entry is a fresh ask,
   * so whatever was dismissed before it is spent — whether the user is
   * returning to the workspace they dismissed or arriving at a different one.
   */
  const beginEntry = useCallback(() => {
    dismissedScopesRef.current = new Set();
  }, []);

  /**
   * THE decision, and the only one: the session just moved to this scope — does
   * the ask belong on screen?
   *
   * Every path that moves the session calls this with the session the server
   * returned for where it now stands: the cold load, `switchTenant`,
   * `enterTenant`, `switchProject` and `selectOrganization`. It is one function
   * rather than a check at each of them because the checks had already drifted
   * apart while there were three: the cold load honoured the app's opt-in and
   * the two entry paths did not, and `enterTenant` asked no staff question at
   * all. Adding two more copies would have been two more places to drift.
   *
   * In order:
   *
   *   1. The app asked for this at all (`asksForWorkspaceAccess`). The auth app
   *      and the staff console do not, and a scope move there is not an ask.
   *   2. A workspace, and a STAFF session. content_reach is a proto3 bool, so a
   *      server that predates it decodes as FALSE rather than absent — without
   *      this a client deployed ahead of the platform would tell every tenant
   *      owner switching project that they cannot read their own workspace.
   *      Non-staff reach is their assignment and was never this prompt's
   *      subject.
   *   3. Does the grant cover where the session now stands? That is the
   *      server's answer, `contentReach`, computed for THIS scope — never
   *      guessed here from the previous one. Covered withdraws both the dialog
   *      and the standing affordance: there is nothing to ask for.
   *   4. Has the ask for this exact scope been dismissed during this entry? The
   *      standing affordance is recorded regardless, so a dismissed scope stays
   *      askable from the bar; only the dialog is held back.
   *
   * No session means no scope moved, so nothing is noted: an ask raised for a
   * workspace the session is not in would name a place the user is not.
   */
  const noteScope = useCallback(
    (context: ConnectSessionContext | null | undefined) => {
      if (!asksForWorkspaceAccess) return;
      if (!context?.tenantId || !holdsPlatformManage(context)) return;
      if (context.contentReach) {
        setReachlessWorkspace(null);
        setImpersonationPrompt(null);
        return;
      }
      const prompt: ImpersonationPrompt = {
        tenantId: context.tenantId,
        // A server that predates the field sends "", and an empty name would
        // render as "You are in  but cannot read its content". Say the neutral
        // thing rather than a blank or a cuid.
        //
        // Off the SAME session that reported the reach, never looked up in
        // `availableTenants`: that list is held in a ref assigned during
        // RENDER, and every caller reaches this from a fetch callback that runs
        // BEFORE that render, so on a cold load the lookup missed and the dialog
        // named a cuid2.
        tenantName:
          context.tenantName || t('common.this_workspace', 'this workspace'),
        pendingGrant: context.pendingGrant ?? null,
        projectId: context.projectId ?? '',
        projectName: context.projectName ?? '',
        orgId: context.orgId ?? '',
        orgName: context.orgName ?? '',
      };
      setReachlessWorkspace(prompt);
      if (dismissedScopesRef.current.has(promptScopeKey(prompt))) return;
      setEverPrompted(true);
      setImpersonationPrompt(prompt);
    },
    [asksForWorkspaceAccess, t],
  );
  noteScopeRef.current = noteScope;

  // Read by the two callbacks below so both can stay dependency-free (and so the
  // context value keeps its stable identity).
  const impersonationPromptRef = useRef<ImpersonationPrompt | null>(null);
  impersonationPromptRef.current = impersonationPrompt;
  const reachlessWorkspaceRef = useRef<ImpersonationPrompt | null>(null);
  reachlessWorkspaceRef.current = reachlessWorkspace;

  const dismissImpersonationPrompt = useCallback(() => {
    const current = impersonationPromptRef.current;
    // Through `promptScopeKey`, never rebuilt here. The decision above tests the
    // key this write produces, so two spellings of it would mean a dismissal
    // that suppresses nothing — silent, and only on the scopes whose spelling
    // differed.
    if (current) dismissedScopesRef.current.add(promptScopeKey(current));
    setImpersonationPrompt(null);
  }, []);

  /**
   * Re-raise the ask from the standing affordance.
   *
   * It raises directly rather than going through `noteScope`, so a dismissal
   * cannot suppress it: the dismissal covers entries the user did not ask
   * for, and this is the one they did.
   */
  const requestWorkspaceAccess = useCallback(() => {
    const target = reachlessWorkspaceRef.current;
    if (!target) return;
    setEverPrompted(true);
    setImpersonationPrompt(target);
  }, []);

  // Identify for error tracking + product analytics whenever the user's
  // identity OR their active workspace/project changes. In-app context switches
  // (switchTenant / switchProject / enterTenant) mutate session state without
  // remounting, so keying on the identity tuple keeps PostHog
  // group('tenant'/'project') pinned to the LIVE context rather
  // than the values captured at page load.
  useEffect(() => {
    if (!user) return;
    onIdentify?.({
      id: user.id,
      email: user.email,
      tenantId: sessionData?.tenantId ?? undefined,
      projectId: sessionData?.projectId ?? undefined,
    });
    // onIdentify is a stable module import in every app; excluding it avoids
    // re-running on unrelated re-renders (same rationale as fetchSession).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.email, sessionData?.tenantId, sessionData?.projectId]);

  const effectivePermissions = sessionData?.effectivePermissions ?? [];
  const workspacePermissions = sessionData?.workspacePermissions ?? [];

  const isLoading = !initialLoadComplete || isMutating;

  const logout = useCallback(async () => {
    try {
      await identity.signOut();
    } catch (err) {
      console.error(
        "Logout error",
        err instanceof Error ? err : new Error(t('auth.logout_failed', 'Logout failed')),
        { component: "SessionContext", operation: "logout" },
      );
    } finally {
      setUser(null);
      setSessionData(null);
      onReset?.();
      await onLogoutCleanup?.();
      triggerAuthRedirect("/login", "logout");
    }
  }, [identity, onReset, onLogoutCleanup, t]);

  const refetchUser = useCallback(async () => {
    await fetchSession();
  }, [fetchSession]);

  // The session context for a place. Nothing is stored: the console names the
  // same place on the requests that follow, through its address bar.
  const readPlace = useCallback(
    (place: { tenantId: string; projectId?: string; orgId?: string }) =>
      identity.getSessionContext(place),
    [identity],
  );

  const switchTenant = useCallback(
    async (tenantId: string) => {
      setIsMutating(true);
      try {
        // Staff reach a workspace they hold no role in through the audited
        // entry, which reports whether they reach its content. It mints
        // nothing: the switch still costs one click, and when it lands without
        // reach the request prompt is raised on this same click rather than
        // leaving the user in a workspace that reads as empty for reasons
        // the screen never states.
        let data: ConnectSessionContext | null;
        if (holdsPlatformManage(sessionDataRef.current)) {
          beginEntry();
          const entry = await identity.enterTenant(tenantId);
          data = entry.context ?? null;
        } else {
          data = await readPlace({ tenantId });
          // A member lands where staff entry lands: the workspace's first
          // project. A read of the workspace names none, so the first project
          // it lists is read too. The console takes the project from the
          // answer into its address bar, and notes, which has no address bar
          // for it, keeps it in the session, so both have a project in scope.
          const first = data?.projectId
            ? null
            : data?.availableProjects.find((p) => p.tenantId === tenantId)?.id;
          if (first) {
            data = (await readPlace({ tenantId, projectId: first })) ?? data;
          }
        }
        noteScope(data);
        if (data) {
          setSessionData(data);
          setUser(data.user ?? null);
          // Every answer is about to be asked for another workspace.
          queryClient.removeQueries();
          return {
            tenantId: data.tenantId ?? null,
            // The workspace's first project, which is where a switch lands
            // when it names none: the entry chose it on the platform, and a
            // read names no project, so it is the first the workspace lists.
            projectId:
              data.projectId ??
              data.availableProjects.find((p) => p.tenantId === tenantId)?.id ??
              null,
            availableProjects: data.availableProjects,
          };
        }
        return undefined;
      } finally {
        setIsMutating(false);
      }
    },
    [identity, queryClient, readPlace, noteScope, beginEntry],
  );

  const switchProject = useCallback(
    async (projectId: string) => {
      const tenantId = sessionDataRef.current?.tenantId;
      if (!tenantId) return undefined;
      setIsMutating(true);
      try {
        const data = await readPlace({ tenantId, projectId });
        // A move into a project the grant does not cover is asked about exactly
        // as arriving there cold would be, and a move into one it does cover
        // withdraws the ask. The platform derived reach for the new project.
        noteScope(data);
        if (data) {
          setSessionData(data);
          setUser(data.user ?? null);
          queryClient.removeQueries();
          return {
            tenantId: data.tenantId ?? null,
            projectId: data.projectId ?? null,
          };
        }
        return undefined;
      } finally {
        setIsMutating(false);
      }
    },
    [queryClient, readPlace, noteScope],
  );

  const selectOrganization = useCallback(
    async (orgId: string | null) => {
      const current = sessionDataRef.current;
      if (!current?.tenantId || !current.projectId) return;
      setIsMutating(true);
      try {
        // Refused (a suspended organisation, one outside the caller's reach)
        // throws before this line and moves nothing, so it notes nothing.
        const data = await readPlace({
          tenantId: current.tenantId,
          projectId: current.projectId,
          orgId: orgId ?? undefined,
        });
        noteScope(data);
        if (data) {
          setSessionData(data);
          setUser(data.user ?? null);
        }
      } finally {
        setIsMutating(false);
      }
    },
    [readPlace, noteScope],
  );

  const enterTenant = useCallback(
    async (tenantId: string, projectId?: string) => {
      setIsMutating(true);
      try {
        beginEntry();
        const { context, grant } = await identity.enterTenant(
          tenantId,
          projectId,
        );
        noteScope(context);
        if (context) {
          setSessionData(context);
          setUser(context.user ?? null);
          queryClient.removeQueries();
          return {
            tenantId: context.tenantId ?? null,
            projectId: context.projectId ?? null,
            availableProjects: context.availableProjects,
            grant,
          };
        }
        return undefined;
      } finally {
        setIsMutating(false);
      }
    },
    [identity, queryClient, noteScope, beginEntry],
  );

  // ── Stable memoization ────────────────────────────────────────
  const tenantId = sessionData?.tenantId ?? null;
  const projectId = sessionData?.projectId ?? null;
  const orgId = sessionData?.orgId ?? null;
  const orgName = sessionData?.orgName ?? null;
  const platformPermissions = sessionData?.platformPermissions ?? [];
  const isStaff = holdsPlatformManage(sessionData);

  const availableTenantsRef = useRef<Array<TenantInfo>>([]);
  const availableProjectsRef = useRef<Array<ProjectInfo>>([]);
  if (sessionData?.availableTenants) {
    availableTenantsRef.current = sessionData.availableTenants;
  }
  if (sessionData?.availableProjects) {
    availableProjectsRef.current = sessionData.availableProjects;
  }
  const availableTenantsCount = availableTenantsRef.current.length;
  const availableProjectsCount = availableProjectsRef.current.length;

  // Offered only while the session is actually IN the workspace the server
  // refused content for. Gating on the session's own workspace is what retires
  // the offer on the way out: `logout` drops the workspace without going
  // through an entry, so it reports no reach.
  const workspaceAccess = useMemo<WorkspaceAccessRequest | null>(() => {
    if (!reachlessWorkspace || reachlessWorkspace.tenantId !== tenantId) {
      return null;
    }
    return { ...reachlessWorkspace, request: requestWorkspaceAccess };
  }, [reachlessWorkspace, tenantId, requestWorkspaceAccess]);

  const errorMessage = error?.message;

  const value: SessionContextValue = useMemo(
    () => {
      const availableTenants = availableTenantsRef.current;
      const availableProjects = availableProjectsRef.current;
      const projectsInCurrentTenant = availableProjects.filter(
        (p) => p.tenantId === tenantId,
      );

      return {
        user,
        effectivePermissions,
        platformPermissions,
        workspacePermissions,
        isAuthenticated: !!user,
        isLoading,
        isInitialLoading: !initialLoadComplete,
        error,

        logout,
        refetchUser,

        tenantId,
        projectId,
        orgId,
        orgName,

        availableTenants,
        availableProjects,
        projectsInCurrentTenant,

        switchTenant,
        switchProject,
        selectOrganization,
        enterTenant,

        workspaceAccess,

        canSelectTenant:
          availableTenantsCount > 1 ||
          (isStaff && !tenantId && availableTenantsCount > 0),
        canSelectProject: projectsInCurrentTenant.length >= 1,
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      user,
      effectivePermissions,
      platformPermissions,
      workspacePermissions,
      isLoading,
      initialLoadComplete,
      errorMessage,
      logout,
      refetchUser,
      switchTenant,
      switchProject,
      selectOrganization,
      enterTenant,
      workspaceAccess,
      tenantId,
      projectId,
      orgId,
      orgName,
      availableTenantsCount,
      availableProjectsCount,
      isStaff,
    ],
  );

  return (
    <SessionContext.Provider value={value}>
      {children}
      {/* Mounted once, here, so every route into a workspace raises it — the
          switcher, a `?tenant=` deep link, and a path-scoped console URL all go
          through the entry path above. An app that had to opt in would be an
          app that could forget to.

          `everPrompted` gates the FIRST mount only, never the close: once
          raised it stays mounted, so a prompt returning to null still closes
          through the dialog's own exit rather than vanishing with it. */}
      {everPrompted && (
        <Suspense fallback={null}>
          <ImpersonationRequestDialog
            prompt={impersonationPrompt}
            onDismiss={dismissImpersonationPrompt}
            // The request just made is the one to resume. The session's own
            // pendingGrant was read before it existed, so without this the
            // bar's ask reopened on a fresh reason form.
            onRequested={(grant) =>
              setReachlessWorkspace((current) =>
                current && current.tenantId === grant.tenantId
                  ? { ...current, pendingGrant: grant }
                  : current,
              )
            }
            onActivated={async () => {
              setImpersonationPrompt(null);
              // Reach is live, so there is nothing left to offer. The dismissal
              // needs no reset: it is spent by the next entry either way.
              //
              // Clear it here rather than relying on the fetchSession() below.
              // That call DOES noteScope now and would clear the
              // banner on a reaching session — but only for an app that asks
              // for workspace access at all, and only after a round trip. This
              // line is what makes the banner go the moment the grant does.
              // `request-access-approval.test.tsx` covers it.
              setReachlessWorkspace(null);
              // The grant is live at the row layer now, so everything already
              // fetched was fetched without reach and reads as empty.
              queryClient.removeQueries();
              await fetchSession();
            }}
          />
        </Suspense>
      )}
      {/* The standing ask. Mounted here for the same reason as the
          dialog: every route into a workspace goes through the entry path
          above, and an affordance an app has to opt into is one some app
          forgets — which is how a dismissal became terminal in the first place.
          Hidden while the dialog is up, since it is the same ask. */}
      {workspaceAccess && !impersonationPrompt && (
        <Suspense fallback={null}>
          <ImpersonationRequestBanner
            workspace={workspaceAccess}
            onRequest={workspaceAccess.request}
          />
        </Suspense>
      )}
    </SessionContext.Provider>
  );
}

export function useSessionContext(): SessionContextValue {
  const t = useT();
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error(
      t('auth.usesessioncontext_must_be_used_within_a_sessioncontextprovider', 'useSessionContext must be used within a SessionContextProvider'),
    );
  }
  return context;
}

/**
 * useWorkspaceAccess exposes the standing ask-for-access affordance for the
 * workspace on screen, or null when there is nothing to ask for.
 *
 * The provider renders a persistent affordance already; this is for putting the
 * SAME ask somewhere more specific — most usefully an empty state, which is
 * otherwise indistinguishable from a workspace that simply has no content.
 */
export function useWorkspaceAccess(): WorkspaceAccessRequest | null {
  const { workspaceAccess } = useSessionContext();
  return workspaceAccess;
}

/**
 * useIsStaff reports whether the signed-in person holds
 * `platform:manage` at the platform root: Foir staff.
 *
 * A separate PLANE, not a rung above the tenant. `platform:manage` lives in its
 * own Demesne vocabulary with its own rolestore, which is what structurally
 * prevents a workspace role from ever carrying it, so this can never be
 * answered by anything in `useCaps()`, and should not be. It is the permission
 * the platform's own staff gate reads.
 */
export function useIsStaff(): boolean {
  const { platformPermissions } = useSessionContext();
  return platformPermissions.includes(PLATFORM_MANAGE);
}

const PLATFORM_MANAGE = "platform:manage";

/** Whether a session context holds `platform:manage` at the platform root. */
function holdsPlatformManage(
  context: ConnectSessionContext | null | undefined,
): boolean {
  return context?.platformPermissions?.includes(PLATFORM_MANAGE) ?? false;
}
